import { useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Leaf, Phone } from "lucide-react";
import { COMPANY } from "@/lib/config";

const links = [
  { to: "/", label: "Forside" },
  { to: "/om-os", label: "Om os" },
  { to: "/haekklipning", label: "Hækklipning" },
  { to: "/havearbejde", label: "Havearbejde" },
  { to: "/priser", label: "Priser" },
  { to: "/galleri", label: "Galleri" },
  { to: "/kontakt", label: "Kontakt" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      data-testid="site-navbar"
      className="glass-nav sticky top-0 z-50 border-b border-brand-border/60"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
          <span className="grid place-items-center h-10 w-10 rounded-full bg-brand-primary text-brand-bg">
            <Leaf className="h-5 w-5" strokeWidth={1.8} />
          </span>
          <div className="leading-tight">
            <div className="font-display text-lg font-medium tracking-tight text-brand-text">
              Syddansk Group
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted">
              Have · Hæk · Pleje
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm rounded-full transition-colors ${
                  isActive
                    ? "text-brand-primary bg-brand-soft"
                    : "text-brand-text hover:text-brand-primary hover:bg-brand-soft/60"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${COMPANY.phone}`}
            data-testid="nav-phone"
            className="text-sm text-brand-text hover:text-brand-primary flex items-center gap-2"
          >
            <Phone className="h-4 w-4" />
            {COMPANY.phoneDisplay}
          </a>
          <Link
            to="/kontakt"
            data-testid="nav-cta-quote"
            className="inline-flex items-center rounded-full bg-brand-primary text-brand-bg px-5 py-2.5 text-sm font-medium hover:bg-brand-hover transition-transform hover:-translate-y-0.5"
          >
            Få et tilbud
          </Link>
        </div>

        <button
          data-testid="nav-toggle"
          className="lg:hidden p-2 -mr-2"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-brand-border/60 bg-brand-bg/95 backdrop-blur-xl">
          <div className="px-6 py-4 space-y-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-xl text-base ${
                    isActive
                      ? "bg-brand-primary text-brand-bg"
                      : "text-brand-text hover:bg-brand-soft"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/kontakt"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-cta"
              className="block text-center mt-3 rounded-full bg-brand-primary text-brand-bg px-5 py-3 font-medium"
            >
              Få et tilbud
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
